import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Avatar from '../../components/Avatar';
import HelpTooltip from '../../components/HelpTooltip';

export default function Roster() {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetPlayer, setResetPlayer] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [teamInfo, setTeamInfo] = useState(null);
  const [consentPlayer, setConsentPlayer] = useState(null);
  const [consentForm, setConsentForm] = useState({ parent_name: '', document_reference: '' });
  const [actionMsg, setActionMsg] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [copyMsg, setCopyMsg] = useState('');
  const [sortBy, setSortBy] = useState('last_name');
  const [editPointsPlayer, setEditPointsPlayer] = useState(null);
  const [editPointsValue, setEditPointsValue] = useState('');
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [addPlayerForm, setAddPlayerForm] = useState({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });
  const [addPlayerError, setAddPlayerError] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  const [loadError, setLoadError] = useState(null);
  const [isPaid, setIsPaid] = useState(true);

  const loadPlayers = async () => {
    setLoadError(null);
    try {
      const data = await apiFetch('/api/admin/players');
      setPlayers(data.players);
    } catch (err) { console.error(err); setLoadError(err.message || 'Failed to load roster'); }
    finally { setLoading(false); }
  };

  const loadTeamInfo = async () => {
    try {
      const data = await apiFetch('/api/admin/teams/current');
      setTeamInfo(data.team);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { setLoading(true); loadPlayers(); loadTeamInfo(); apiFetch('/api/admin/is-paid').then(d => setIsPaid(d.isPaid)).catch(() => {}); }, [apiFetch]); // eslint-disable-line

  const handleToggle = async (id, active) => {
    try {
      await apiFetch(`/api/admin/players/${id}/${active ? 'deactivate' : 'activate'}`, { method: 'PUT' });
      loadPlayers();
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async (player) => {
    if (!window.confirm(`Permanently delete ${player.first_name} ${player.last_name}? This removes all their completions and badges.`)) return;
    try {
      await apiFetch(`/api/admin/players/${player.id}`, { method: 'DELETE' });
      loadPlayers();
    } catch (err) { alert(err.message); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch(`/api/admin/players/${resetPlayer.id}/reset-password`, {
        method: 'PUT',
        body: JSON.stringify({ password: resetPw }),
      });
      setResetPlayer(null);
      setResetPw('');
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleUnder13Toggle = async () => {
    const newVal = !teamInfo?.has_under_13;
    const msg = newVal
      ? 'Enable under-13 consent? Players without granted consent will be locked until a parent consents.'
      : 'Disable under-13 consent? All awaiting players will be unlocked.';
    if (!window.confirm(msg)) return;
    try {
      await apiFetch(`/api/admin/teams/${teamInfo.id}/under-13`, {
        method: 'PUT',
        body: JSON.stringify({ has_under_13: newVal }),
      });
      setActionMsg(newVal ? 'Under-13 consent enabled. Consent emails sent to parents on file.' : 'Under-13 consent disabled. Players unlocked.');
      loadTeamInfo();
      loadPlayers();
      setTimeout(() => setActionMsg(''), 5000);
    } catch (err) { alert(err.message); }
  };

  const handleSendConsent = async (player) => {
    if (!player.parent_email) {
      const email = window.prompt(`Enter parent email for ${player.first_name} ${player.last_name}:`);
      if (!email) return;
      try {
        await apiFetch(`/api/admin/players/${player.id}/update-parent-email`, {
          method: 'PUT',
          body: JSON.stringify({ parent_email: email }),
        });
      } catch (err) { alert(err.message); return; }
    }
    try {
      await apiFetch(`/api/admin/players/${player.id}/send-consent`, { method: 'POST' });
      setActionMsg(`Consent email sent for ${player.first_name}.`);
      setTimeout(() => setActionMsg(''), 5000);
    } catch (err) { alert(err.message); }
  };

  const handleRecordConsent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch(`/api/admin/players/${consentPlayer.id}/record-consent`, {
        method: 'POST',
        body: JSON.stringify(consentForm),
      });
      setConsentPlayer(null);
      setConsentForm({ parent_name: '', document_reference: '' });
      setActionMsg('Consent recorded. Player activated.');
      loadPlayers();
      setTimeout(() => setActionMsg(''), 5000);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleSetPoints = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch(`/api/admin/players/${editPointsPlayer.id}/set-points`, {
        method: 'PUT',
        body: JSON.stringify({ lifetime_points: parseInt(editPointsValue, 10) }),
      });
      setEditPointsPlayer(null);
      setEditPointsValue('');
      setActionMsg(`Career total updated for ${editPointsPlayer.first_name}.`);
      loadPlayers();
      setTimeout(() => setActionMsg(''), 5000);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const canAddPlayers = isPaid && (teamInfo?.billing_model === 'club_pays' || teamInfo?.is_free);

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    setAddPlayerError('');
    setSaving(true);
    try {
      const { birth_year, ...rest } = addPlayerForm;
      await apiFetch('/api/admin/players', {
        method: 'POST',
        body: JSON.stringify({ ...rest, ...(birth_year ? { birth_year: parseInt(birth_year, 10) } : {}) }),
      });
      setShowAddPlayer(false);
      setAddPlayerForm({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });
      setActionMsg('Player added successfully.');
      loadPlayers();
      setTimeout(() => setActionMsg(''), 5000);
    } catch (err) { setAddPlayerError(err.message); }
    finally { setSaving(false); }
  };

  const parseImportCSV = (text) => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return [];
    let start = 0;
    const firstLine = lines[0].toLowerCase();
    if (firstLine.includes('team') && firstLine.includes('name')) start = 1;
    return lines.slice(start).map(line => {
      const cells = line.split(',').map(c => c.trim());
      return { first_name: cells[3] || '', last_name: cells[4] || '', email: cells[5] || '' };
    });
  };

  const handleImportPreview = async () => {
    setImportError('');
    setImportResult(null);
    const rows = parseImportCSV(csvText);
    if (rows.length === 0) { setImportError('No data to import.'); return; }
    setSaving(true);
    try {
      const data = await apiFetch('/api/admin/players/import', {
        method: 'POST',
        body: JSON.stringify({ players: rows, preview: true }),
      });
      setImportPreview(data);
    } catch (err) { setImportError(err.message); }
    finally { setSaving(false); }
  };

  const handleImportCommit = async () => {
    setImportError('');
    const rows = parseImportCSV(csvText);
    setSaving(true);
    try {
      const data = await apiFetch('/api/admin/players/import', {
        method: 'POST',
        body: JSON.stringify({ players: rows, preview: false }),
      });
      setImportResult(data);
      setImportPreview(null);
      setCsvText('');
      loadPlayers();
    } catch (err) { setImportError(err.message); }
    finally { setSaving(false); }
  };

  const downloadSampleCSV = () => {
    const csv = [
      'Team Name,Under 13?,Coach or Player,First Name,Last Name,Email',
      'U10 Lightning,Yes,Coach,Jane,Smith,jane.smith@example.com',
      'U10 Lightning,Yes,Player,Tommy,Johnson,parent@example.com',
      'U10 Lightning,Yes,Player,Sarah,Williams,',
      'U14 Elite,No,Coach,Mike,Davis,mike.davis@example.com',
      'U14 Elite,No,Player,Alex,Brown,',
      'U14 Elite,No,Player,Jordan,Lee,',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-player-import.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const consentBadge = (status) => {
    switch (status) {
      case 'granted': return { text: 'Consented', bg: '#d4edda', color: '#155724' };
      case 'awaiting': return { text: 'Awaiting Consent', bg: '#fff3cd', color: '#856404' };
      case 'revoked': return { text: 'Revoked', bg: '#f8d7da', color: '#721c24' };
      case 'not_required': return null;
      default: return null;
    }
  };

  if (loading) return <div className="admin-page"><div className="loading"><div className="spinner" /></div></div>;

  return (
    <div className="admin-page">
      <div className="flex-between mb-16">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1 className="page-title" style={{ marginBottom: 0 }}>Roster</h1>
          <HelpTooltip>
            <h3>Managing Your Roster</h3>
            <p>This page shows all the players on your team. You can see each player's status, points, and streak.</p>
            <p><strong>Invite Players</strong> — Send a sign-up link to players or parents. They'll create an account and join your team.{canAddPlayers ? '' : ' Parents will purchase a subscription when they sign up.'}</p>
            {canAddPlayers && <p><strong>Add Player</strong> — Directly create a player account on your team without sending an invite.</p>}
            {canAddPlayers && <p><strong>Import Roster</strong> — Paste CSV data to add multiple players at once. Great for setting up a full team quickly.</p>}
            <p><strong>Export Roster</strong> — Download your roster as a CSV file with player names, emails, and statuses.</p>
            <p>Click any player's name to view their profile, reset their password, or adjust their points.</p>
          </HelpTooltip>
        </div>
        <div className="roster-actions" style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-blue btn-sm" onClick={() => { setShowInvite(true); setCopyMsg(''); }}>Invite Players</button>
          {canAddPlayers && (
            <>
              <button className="btn btn-orange btn-sm" onClick={() => { setShowAddPlayer(true); setAddPlayerForm({ first_name: '', last_name: '', username: '', password: '', parent_email: '' }); setAddPlayerError(''); }} disabled={teamInfo?.is_demo} style={teamInfo?.is_demo ? { opacity: 0.4, cursor: 'not-allowed' } : undefined} title={teamInfo?.is_demo ? 'This is not available on demo accounts' : undefined}>Add Player</button>
              <button className="btn btn-sm" style={{ background: '#fff', color: '#2563eb', border: '1px solid #2563eb', ...(teamInfo?.is_demo ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }} onClick={() => { setShowImport(true); setCsvText(''); setImportPreview(null); setImportResult(null); setImportError(''); }} disabled={teamInfo?.is_demo} title={teamInfo?.is_demo ? 'This is not available on demo accounts' : undefined}>Import Players</button>
            </>
          )}
          <button className="btn btn-outline btn-sm" onClick={() => {
            const csvHeader = 'First Name,Last Name,Email,Status,Date and Time Signed Up';
            const csvRows = players.map(p => {
              const email = p.player_email || p.parent_email || '';
              let status = 'Active';
              if (p.consent_status === 'awaiting') status = 'Awaiting Consent';
              else if (p.status === 'inactive') status = 'Inactive';
              const signedUp = p.created_at ? new Date(p.created_at).toLocaleString() : '';
              return [p.first_name, p.last_name, email, status, signedUp]
                .map(v => `"${String(v).replace(/"/g, '""')}"`)
                .join(',');
            });
            const csv = [csvHeader, ...csvRows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'roster-export.csv'; a.click();
            URL.revokeObjectURL(url);
          }}>Export Roster</button>
        </div>
      </div>


      <div className="roster-sort" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.8rem', color: '#888' }}>Sort by:</span>
        <button
          className={`btn btn-sm ${sortBy === 'first_name' ? 'btn-blue' : 'btn-outline'}`}
          onClick={() => setSortBy('first_name')}
        >First Name</button>
        <button
          className={`btn btn-sm ${sortBy === 'last_name' ? 'btn-blue' : 'btn-outline'}`}
          onClick={() => setSortBy('last_name')}
        >Last Name</button>
        {teamInfo?.join_code && (
          <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: 8 }}>
            Player Login Link: <a href={`${window.location.origin}/t/${teamInfo.join_code}`} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>{`${window.location.origin}/t/${teamInfo.join_code}`}</a>
          </span>
        )}
      </div>

      {loadError && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.9em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{loadError}</span>
          <button className="btn btn-sm btn-outline" onClick={() => { setLoading(true); loadPlayers(); }}>Retry</button>
        </div>
      )}

      {actionMsg && (
        <div style={{ background: '#f39c12', color: '#fff', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.9em', textAlign: 'center' }}>
          {actionMsg}
        </div>
      )}

      {[...players].sort((a, b) => a[sortBy].localeCompare(b[sortBy])).map((p) => {
        const badge = consentBadge(p.consent_status);
        return (
          <div key={p.id} className={`player-row ${!p.active ? 'inactive' : ''}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 0', minWidth: 0 }}>
              <Avatar firstName={p.first_name} lastName={p.last_name} level={p.level} latestBadgeEmoji={p.latest_badge_emoji} size={34} />
              <div className="player-info">
                <div className="player-name">
                  <span onClick={() => navigate(`/admin/player/${p.id}`)} style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--text-muted)', textUnderlineOffset: 2 }}>{p.first_name} {p.last_name}</span>
                  {badge && (
                    <span style={{ fontSize: '0.7em', fontWeight: 600, padding: '2px 6px', borderRadius: 4, marginLeft: 8, background: badge.bg, color: badge.color }}>
                      {badge.text}
                    </span>
                  )}
                </div>
                <div className="player-username">
                  @{p.username}{!p.active ? ' (inactive)' : ''}
                  {p.parent_email && <span style={{ color: '#999', marginLeft: 6, fontSize: '0.85em' }}>· {p.parent_email}</span>}
                </div>
              </div>
            </div>
            <div className="player-stats" style={{ display: 'flex', gap: 16, alignItems: 'center', marginRight: 16, flexShrink: 0 }}>
              <div style={{ textAlign: 'center', minWidth: 50 }}>
                <div style={{ fontSize: '0.7rem', color: '#888', lineHeight: 1 }}>Challenge</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.season_points}</div>
              </div>
              <div style={{ textAlign: 'center', minWidth: 50, cursor: 'pointer' }} onClick={() => { setEditPointsPlayer(p); setEditPointsValue(String(p.lifetime_points || 0)); }} title="Click to edit">
                <div style={{ fontSize: '0.7rem', color: '#888', lineHeight: 1 }}>Total</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', textDecoration: 'underline', textDecorationColor: '#ccc', textUnderlineOffset: 2 }}>{p.lifetime_points || 0}</div>
              </div>
              <div style={{ textAlign: 'center', minWidth: 50 }}>
                <div style={{ fontSize: '0.7rem', color: '#888', lineHeight: 1 }}>Streak</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.current_streak}</div>
              </div>
            </div>
            <div className="player-actions">
              {p.consent_status === 'awaiting' && (
                <>
                  <button className="btn btn-blue btn-sm" onClick={() => handleSendConsent(p)}>
                    Send Consent
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => { setConsentPlayer(p); setConsentForm({ parent_name: '', document_reference: '' }); }}>
                    Record Consent
                  </button>
                </>
              )}
              {p.active ? (
                <>
                  <button className="btn btn-outline btn-sm" onClick={() => { setResetPlayer(p); setResetPw(''); }}>
                    Reset PW
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => handleToggle(p.id, true)} disabled={teamInfo?.is_demo} title={teamInfo?.is_demo ? 'Demo accounts cannot be modified' : undefined}>
                    Deactivate
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p)} disabled={teamInfo?.is_demo} title={teamInfo?.is_demo ? 'Demo accounts cannot be deleted' : undefined} style={teamInfo?.is_demo ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}>
                    Delete
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-blue btn-sm" onClick={() => handleToggle(p.id, false)}>
                    Activate
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p)} disabled={teamInfo?.is_demo} title={teamInfo?.is_demo ? 'Demo accounts cannot be deleted' : undefined} style={teamInfo?.is_demo ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}>
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}

      {players.length === 0 && <div className="no-season-msg">No players added yet.</div>}

      {resetPlayer && (
        <div className="modal-overlay" onClick={() => setResetPlayer(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Reset Password for {resetPlayer.first_name}</h2>
            <form onSubmit={handleReset}>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input className="form-input" type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setResetPlayer(null)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Saving...' : 'Reset Password'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {consentPlayer && (
        <div className="modal-overlay" onClick={() => setConsentPlayer(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Record Consent for {consentPlayer.first_name} {consentPlayer.last_name}</h2>
            <p style={{ color: '#666', fontSize: '0.9em', marginBottom: 16 }}>
              Use this if consent was provided via a signed paper form or other document.
            </p>
            <form onSubmit={handleRecordConsent}>
              <div className="form-group">
                <label className="form-label">Parent/Guardian Name</label>
                <input className="form-input" value={consentForm.parent_name} onChange={(e) => setConsentForm({...consentForm, parent_name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">Document Reference <span style={{ fontWeight: 400, color: '#666' }}>(e.g. "Signed form 6/20/2026")</span></label>
                <input className="form-input" value={consentForm.document_reference} onChange={(e) => setConsentForm({...consentForm, document_reference: e.target.value})} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setConsentPlayer(null)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Saving...' : 'Record Consent'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showInvite && teamInfo && (
        <div className="modal-overlay" onClick={() => setShowInvite(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ color: '#000000' }}>
            <h2>Invite Players</h2>
            <p style={{ fontSize: '0.9em', color: '#666', marginBottom: 16 }}>
              Share this link with parents so they can sign up their player and join <strong>{teamInfo.name}</strong>.
            </p>
            <div style={{ background: '#f5f5f5', padding: '12px 16px', borderRadius: 8, fontSize: '0.85rem', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: 12 }}>
              {`${window.location.origin}/parents/join/${teamInfo.join_code}`}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn btn-blue btn-sm" onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/parents/join/${teamInfo.join_code}`);
                setCopyMsg('Copied!');
                setTimeout(() => setCopyMsg(''), 2000);
              }}>
                Copy Link
              </button>
              {copyMsg && <span style={{ color: '#2ecc71', fontSize: '0.85em', fontWeight: 600 }}>{copyMsg}</span>}
            </div>
            <p style={{ fontSize: '0.8em', color: '#999', marginTop: 16 }}>
              Parents will create an account and add their player to your team.
            </p>
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowInvite(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editPointsPlayer && (
        <div className="modal-overlay" onClick={() => setEditPointsPlayer(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Career Total for {editPointsPlayer.first_name} {editPointsPlayer.last_name}</h2>
            <form onSubmit={handleSetPoints}>
              <div className="form-group">
                <label className="form-label">Lifetime Points</label>
                <input className="form-input" type="number" min="0" value={editPointsValue} onChange={(e) => setEditPointsValue(e.target.value)} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setEditPointsPlayer(null)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddPlayer && (
        <div className="modal-overlay" onClick={() => setShowAddPlayer(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add Player to {teamInfo?.name}</h2>
            {addPlayerError && <div style={{ background: '#f8d7da', color: '#721c24', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: '0.9em' }}>{addPlayerError}</div>}
            <form onSubmit={handleAddPlayer}>
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input className="form-input" value={addPlayerForm.first_name} onChange={(e) => setAddPlayerForm({...addPlayerForm, first_name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input className="form-input" value={addPlayerForm.last_name} onChange={(e) => setAddPlayerForm({...addPlayerForm, last_name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">Birth Year <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                <select className="form-input" value={addPlayerForm.birth_year} onChange={(e) => setAddPlayerForm({...addPlayerForm, birth_year: e.target.value})}>
                  <option value="">--</option>
                  {Array.from({ length: 23 }, (_, i) => { const y = new Date().getFullYear() - 3 - i; return <option key={y} value={y}>{y}</option>; })}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Username</label>
                <input className="form-input" value={addPlayerForm.username} onChange={(e) => setAddPlayerForm({...addPlayerForm, username: e.target.value})} required autoCapitalize="none" />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="text" value={addPlayerForm.password} onChange={(e) => setAddPlayerForm({...addPlayerForm, password: e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">{teamInfo?.has_under_13 ? 'Parent Email' : 'Email'}</label>
                <input className="form-input" type="email" value={addPlayerForm.parent_email} onChange={(e) => setAddPlayerForm({...addPlayerForm, parent_email: e.target.value})} autoCapitalize="none" />
                {teamInfo?.has_under_13 && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Required for under-13 consent</div>}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowAddPlayer(false)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Adding...' : 'Add Player'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImport && (
        <div className="modal-overlay" onClick={() => setShowImport(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2>Import Players to {teamInfo?.name}</h2>
            <p style={{ fontSize: '0.85em', color: '#666', marginBottom: 12 }}>
              Paste CSV data below or upload a file. Format: <strong>Team Name, Under 13?, Coach or Player, First Name, Last Name, Email</strong> (email is optional for players).
              Usernames and passwords are auto-generated.
            </p>
            <div style={{ marginBottom: 12 }}>
              <button className="btn btn-orange btn-sm" onClick={downloadSampleCSV}>Download Sample CSV</button>
            </div>
            <div className="form-group">
              <label className="form-label">Upload CSV File</label>
              <input type="file" accept=".csv" onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => { setCsvText(ev.target.result); setImportPreview(null); setImportResult(null); setImportError(''); };
                reader.readAsText(file);
              }} />
            </div>
            <div className="form-group">
              <label className="form-label">Or Paste CSV Data</label>
              <textarea className="form-input" rows={6} value={csvText} onChange={(e) => { setCsvText(e.target.value); setImportPreview(null); setImportResult(null); setImportError(''); }}
                placeholder={'Team Name,Under 13?,Coach or Player,First Name,Last Name,Email\nU10 Lightning,Yes,Player,Tommy,Johnson,parent@example.com\nU10 Lightning,Yes,Player,Sarah,Williams,'}
                style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
            </div>
            {importError && <div style={{ background: '#f8d7da', color: '#721c24', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: '0.9em' }}>{importError}</div>}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button className="btn btn-blue btn-sm" onClick={handleImportPreview} disabled={saving || !csvText.trim()}>
                {saving ? 'Checking...' : 'Preview Import'}
              </button>
              {importPreview && importPreview.summary && importPreview.summary.errors.length === 0 && (
                <button className="btn btn-orange btn-sm" onClick={handleImportCommit} disabled={saving}>
                  {saving ? 'Importing...' : `Import ${importPreview.summary.players_to_create} Player${importPreview.summary.players_to_create !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
            {importPreview && (
              <div style={{ background: '#f0f4ff', padding: 12, borderRadius: 8, fontSize: '0.85em', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Preview</div>
                <div>Players to create: {importPreview.summary.players_to_create}</div>
                {importPreview.summary.duplicates_skipped > 0 && (
                  <div style={{ color: '#6c757d' }}>Existing players skipped: {importPreview.summary.duplicates_skipped}</div>
                )}
                {importPreview.summary.errors.length > 0 && (
                  <div style={{ color: '#dc3545', marginTop: 6 }}>
                    {importPreview.summary.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
                  </div>
                )}
                {importPreview.summary.warnings.length > 0 && (
                  <div style={{ color: '#856404', marginTop: 6 }}>
                    {importPreview.summary.warnings.map((w, i) => <div key={i}>Row {w.row}: {w.warning}</div>)}
                  </div>
                )}
                {importPreview.players && importPreview.players.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {importPreview.players.map((p, i) => (
                      <div key={i} style={{ fontSize: '0.85em', color: '#333' }}>{p.first_name} {p.last_name} &rarr; @{p.username}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {importResult && (
              <div style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', padding: 12, borderRadius: 8, fontSize: '0.85em' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Import Complete</div>
                <div>{importResult.summary.players_created} player{importResult.summary.players_created !== 1 ? 's' : ''} created.</div>
                {importResult.credentials && importResult.credentials.length > 0 && (
                  <button className="btn btn-outline btn-sm" style={{ marginTop: 8 }} onClick={() => {
                    const header = 'First Name,Last Name,Username,Password';
                    const rows = importResult.credentials.map(c =>
                      [c.first_name, c.last_name, c.username, c.password].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
                    );
                    const csv = [header, ...rows].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'player-credentials.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}>Download Player Logins</button>
                )}
              </div>
            )}
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowImport(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
